<template>
	<TnCard
		data-cy-payment-summary-card
		border
		class="payment-summary-container"
	>
		<PaymentSummaryTotal
			:monthlyAmount="paymentSummary.totalMonthly"
			:oneTimeAmount="paymentSummary.totalOneTime"
		/>
		<hr
			v-if="showDetails"
			class="divider margin-top-s"
		/>
		<div
			v-if="showDetails"
			class="expandable margin-top-s"
		>
			<div
				class="expandable-header"
				@click="expandableOpen = !expandableOpen"
			>
				<TnParagraph
					:bold="expandableOpen"
					size="s"
				>
					Se alle priser og rabatter
				</TnParagraph>
				<TnIcon
					class="icon"
					:name="expandIcon"
				/>
			</div>
			<div
				class="expandable-body margin-top-s"
				:class="{ open: expandableOpen }"
			>
				<div class="payment-lines">
					<PaymentSummaryLine
						v-for="line in paymentSummary.paymentLines"
						:key="line.id"
						:hasBorder="true"
					>
						<!-- LEFT COLUMN -->
						<template #line-left>
							<TnParagraph size="s">{{ line.name }}</TnParagraph>

							<!-- Description -->
							<TnParagraph
								v-if="line.description"
								size="xs"
								class="line-description"
							>
								{{ line.description }}
							</TnParagraph>

							<!-- Total price to be displayed in case of down payment -->
							<TnParagraph
								v-if="line.totalPrice"
								size="xs"
								class="line-description"
							>
								{{ `Totalpris ${line.totalPrice} ` }}
								<span
									v-if="line.beforeTotalPrice"
									class="strike-through"
								>
									{{ `(${line.beforeTotalPrice})` }}
								</span>
							</TnParagraph>
						</template>

						<!-- RIGHT COLUMN -->
						<template #line-right>
							<TnParagraph size="s">
								{{ line.price }}
								<span
									v-if="line.beforePrice"
									class="strike-through"
								>
									{{ `(${line.beforePrice})` }}
								</span>
							</TnParagraph>
							<TnParagraph
								v-if="line.subPrice"
								size="xs"
								class="line-description"
							>
								{{ line.subPrice }}
							</TnParagraph>
						</template>
					</PaymentSummaryLine>

					<!-- Total money saved-->
					<hr
						class="divider-bold"
						v-if="totalSaved"
					/>
					<div
						class="total-saved"
						v-if="totalSaved"
					>
						<PaymentSummaryLine>
							<template #line-left>
								<TnParagraph
									bold
									size="s"
								>
									Sum rabatter
								</TnParagraph>
							</template>
							<template #line-right>
								<TnParagraph
									bold
									size="s"
								>
									{{ totalSaved }}
								</TnParagraph>
							</template>
						</PaymentSummaryLine>
					</div>
				</div>
			</div>
		</div>
	</TnCard>
</template>

<script setup lang="ts">
import type { PaymentSummary } from "~/components/ecommerce/types/PaymentSummary";
import { formatPrice } from "~/helpers/formatting/price";

const { paymentSummary, showDetails = false } = defineProps<{
	paymentSummary: PaymentSummary;
	showDetails: boolean;
}>();

const expandableOpen = ref<boolean>(false);
const expandIcon = computed<string>(() => (expandableOpen.value ? "chevron-up" : "chevron-down"));

const totalSaved = computed<string>(() =>
	paymentSummary.totalSaved ? `-${formatPrice.oneTime(paymentSummary.totalSaved)}` : undefined,
);
</script>

<style scoped lang="scss">
.card.payment-summary-container {
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	padding: $spacing-m !important;
}

.price-discount {
	text-decoration: line-through;
	color: $color-neutrals-400-tint;
}

.border {
	display: block;
	margin: auto;
	width: 100%;
	border-radius: 4px;
}

.icon {
	pointer-events: none;
}

.divider {
	border: 1px solid $color-neutrals-100-tint;
}
.divider-bold {
	border-top: 2px solid $color-neutrals-400-tint;
}

.line-description {
	color: $color-neutrals-700-shade;
}
.strike-through {
	text-decoration: line-through;
	color: $color-neutrals-400-tint;
}

.expandable {
	margin-bottom: -12px;
	&-header {
		cursor: pointer;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&-body {
		display: grid;
		grid-template-rows: 0fr;
		overflow: hidden;
		transition: grid-template-rows 200ms;

		div {
			overflow: hidden;
		}

		.payment-lines {
			display: flex;
			flex-direction: column;
		}

		&.open {
			grid-template-rows: 1fr;
		}
	}
}

.total-saved {
	padding-top: $spacing-s;
	padding-bottom: $spacing-s;
	.payment-summary-line {
		padding-top: $spacing-2xs;
		padding-bottom: $spacing-2xs;
		background-color: $color-primary-superlight;
	}
}
</style>
